import challengeConfig from '../config/challengeConfig';

const PREF_CATEGORIES = ['difficulty', 'primary', 'secondary', 'grenade', 'stratagem'];

function getLatestConfig() {
  const versions = challengeConfig.versions;
  const latestVersion = versions[versions.length - 1].version;
  return challengeConfig[latestVersion];
}

/**
 * Align a preference boolean array to the current config list.
 * Existing slots keep their value; newly appended items default to enabled (true).
 * Relies on append-only config lists (see README warbond checklist).
 */
function padCategory(stored, items) {
  if (!Array.isArray(items)) return Array.isArray(stored) ? stored : [];
  const old = Array.isArray(stored) ? stored : [];
  return items.map((_, i) => (i < old.length ? !!old[i] : true));
}

function migratePreferences(preferences, config) {
  let changed = false;
  const next = { ...preferences };

  for (const cat of PREF_CATEGORIES) {
    const padded = padCategory(next[cat], config[cat]);
    const old = Array.isArray(next[cat]) ? next[cat] : [];
    if (
      padded.length !== old.length ||
      padded.some((value, i) => value !== !!old[i])
    ) {
      changed = true;
    }
    next[cat] = padded;
  }

  return { preferences: next, changed };
}

export const getPreferences = () => {
  let preferences = JSON.parse(localStorage.getItem('preferences')) || {
    gamerTag: '',
    difficulty: [],
    primary: [],
    secondary: [],
    grenade: [],
    stratagem: [],
  };

  // Check if the old 'stratagems' field exists
  let renamedStratagems = false;
  if (preferences.stratagems) {
    preferences.stratagem = preferences.stratagems;
    delete preferences.stratagems;
    renamedStratagems = true;
  }

  const { preferences: migrated, changed } = migratePreferences(
    preferences,
    getLatestConfig()
  );

  if (changed || renamedStratagems) {
    savePreferences(migrated);
  }

  return migrated;
};

export const savePreferences = (preferences) => {
  localStorage.setItem('preferences', JSON.stringify(preferences));
};

export const canAcceptChallenge = (challenge, preferences) => {
  const config = challengeConfig[challenge.version];

  if (!config || !challenge.mission || !challenge.loadout) return false;

  const isValid = (category, selected, challengeCategory) => {
    // Ensure `selected` is an array and not undefined
    if (!Array.isArray(selected)) return false;
    return selected.some((item, index) => item && config[category][index] === challengeCategory);
  };

  const validDifficulty = challenge.mission.difficulty
    ? isValid('difficulty', preferences.difficulty || [], challenge.mission.difficulty)
    : false;

  const validPrimary = challenge.loadout.primary
    ? isValid('primary', preferences.primary || [], challenge.loadout.primary)
    : false;

  const validSecondary = challenge.loadout.secondary
    ? isValid('secondary', preferences.secondary || [], challenge.loadout.secondary)
    : false;

  const validGrenade = challenge.loadout.grenade
    ? isValid('grenade', preferences.grenade || [], challenge.loadout.grenade)
    : false;

  const validStratagem1 = challenge.loadout.stratagem1
    ? isValid('stratagem', preferences.stratagem || [], challenge.loadout.stratagem1)
    : false;

  const validStratagem2 = challenge.loadout.stratagem2
    ? isValid('stratagem', preferences.stratagem || [], challenge.loadout.stratagem2)
    : false;

  const validStratagem3 = challenge.loadout.stratagem3
    ? isValid('stratagem', preferences.stratagem || [], challenge.loadout.stratagem3)
    : false;

  const validStratagem4 = challenge.loadout.stratagem4
    ? isValid('stratagem', preferences.stratagem || [], challenge.loadout.stratagem4)
    : false;

  return validDifficulty && validPrimary && validSecondary && validGrenade &&
         validStratagem1 && validStratagem2 && validStratagem3 && validStratagem4;
};

export const ensurePreferencesInitialized = () => {
  // getPreferences migrates/pads against the latest config and persists.
  getPreferences();
};
